// CharityWorkup — the deeper give-back narrative on the unified landing.
// Articulates the variant-tagged, region-matched architecture documented
// in /app/memory/BUSINESS_PLAN.md (§ "The Give-Back Engine").
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";
import { Heart, Globe, Scales } from "@phosphor-icons/react";

const FADE = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] },
};

export function CharityWorkup() {
    const { t } = useTranslation();
    return (
        <section
            className="relative py-32 px-6 bg-white overflow-hidden"
            data-testid="charity-workup-section"
            id="charity"
        >
            <FmnWatermark position="right" size={720} opacity={0.05} rotate={-10} />
            <div className="max-w-6xl mx-auto">
                <motion.div {...FADE} className="text-center max-w-3xl mx-auto mb-14">
                    <div className="flex justify-center mb-6">
                        <Heart size={42} weight="fill" className="text-[#A85D62]" />
                    </div>
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                        {t("charity_workup.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.06] tracking-tight">
                        {t("charity_workup.headline_part1")}{" "}
                        <em className="italic text-[#4A5A95]">{t("charity_workup.headline_emphasis")}</em>
                    </h2>
                    <p className="mt-7 text-lg text-[#5C5A59] leading-relaxed font-light">
                        {t("charity_workup.lead")}
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                    <motion.div
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.1 }}
                        className="rounded-[2rem] bg-gradient-to-br from-[#F4F6FB] via-white to-[#EDF0F8] p-8 sm:p-10 border border-[#8C9ECB]/30 shadow-rem-soft"
                        data-testid="charity-card-dementia"
                    >
                        <div className="flex items-center gap-3 mb-5">
                            <ForgetMeNotIcon size={28} />
                            <span className="text-[10px] uppercase tracking-[0.24em] text-[#4A5A95] font-semibold">
                                Lavender edition
                            </span>
                        </div>
                        <h3 className="font-serif text-2xl text-[#2D2B2A] leading-tight">
                            {t("charity_workup.dementia_card_title")}
                        </h3>
                        <p className="mt-4 text-[#5C5A59] leading-relaxed text-sm">
                            {t("charity_workup.dementia_card_body")}
                        </p>
                    </motion.div>

                    <motion.div
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.22 }}
                        className="rounded-[2rem] bg-gradient-to-br from-[#FBF1F2] via-white to-[#EAF2F9] p-8 sm:p-10 border border-[#F4C7CC]/40 shadow-rem-soft"
                        data-testid="charity-card-kids"
                    >
                        <div className="flex items-center gap-3 mb-5">
                            <ForgetMeNotIcon size={28} />
                            <span className="text-[10px] uppercase tracking-[0.24em] text-[#A85D62] font-semibold">
                                Pink + blue editions
                            </span>
                        </div>
                        <h3 className="font-serif text-2xl text-[#2D2B2A] leading-tight">
                            {t("charity_workup.kids_card_title")}
                        </h3>
                        <p className="mt-4 text-[#5C5A59] leading-relaxed text-sm">
                            {t("charity_workup.kids_card_body")}
                        </p>
                    </motion.div>
                </div>

                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.35 }}
                    className="mt-12 rounded-[2rem] bg-[#FDFBF7] border border-[#E5E1D8] p-8 sm:p-10"
                >
                    <div className="flex items-start gap-4">
                        <div className="shrink-0 mt-1">
                            <Globe size={28} weight="duotone" className="text-[#6B7DB8]" />
                        </div>
                        <div>
                            <p className="font-serif italic text-lg sm:text-xl text-[#2D2B2A] leading-relaxed">
                                &ldquo;{t("charity_workup.region_note")}&rdquo;
                            </p>
                            <p className="mt-5 text-sm text-[#5C5A59] leading-relaxed">
                                {t("charity_workup.footnote")}
                            </p>
                        </div>
                    </div>
                </motion.div>

                <motion.p
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.45 }}
                    className="mt-10 text-center text-sm text-[#8A8786] inline-flex items-center justify-center gap-2 w-full"
                    data-testid="charity-transparency-note"
                >
                    <Scales size={16} weight="duotone" className="text-[#6B7DB8]" />
                    {t("charity_workup.transparency_note")}
                </motion.p>

                {/* Live charity tally — visible from day one, starts at $0 */}
                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.55 }}
                    className="mt-14 rounded-[2rem] bg-gradient-to-br from-[#4A5A95] via-[#2D2B4A] to-[#3E2F4F] text-white p-10 sm:p-14 text-center shadow-rem-card overflow-hidden relative"
                    data-testid="charity-counter"
                >
                    <div className="absolute inset-0 opacity-10 pointer-events-none">
                        <Heart size={280} weight="duotone" className="absolute -right-12 -bottom-12 text-white" />
                    </div>
                    <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-white/70 font-semibold mb-6 relative">
                        {t("charity_workup.counter_eyebrow")}
                    </p>
                    <div className="relative">
                        <p
                            className="font-serif text-7xl sm:text-8xl lg:text-9xl text-white leading-none tracking-tight"
                            data-testid="charity-counter-amount"
                        >
                            {t("charity_workup.counter_amount")}
                        </p>
                        <p className="mt-5 text-base sm:text-lg text-white/80 font-light max-w-2xl mx-auto leading-relaxed">
                            {t("charity_workup.counter_label")}
                        </p>
                        <a
                            href="#interest"
                            className="inline-block mt-7 text-sm sm:text-base font-serif italic text-white border-b border-white/40 hover:border-white pb-1 transition-colors"
                            data-testid="charity-counter-cta"
                        >
                            {t("charity_workup.counter_cta")}
                        </a>
                        <p className="mt-8 text-xs text-white/55 max-w-xl mx-auto leading-relaxed italic">
                            {t("charity_workup.counter_subnote")}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
